import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTractor,
  faWarehouse,
  faHandshake,
  faSeedling,
  faFileContract,
  faGlobe,
  faTimes,
  faPhone,
  faCalendarAlt,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";

// Full, functional, responsive AgriLinkAssets component with dummy data and working buttons/modals
export default function AgriLinkAssets() {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // selected asset card
  const [modalMode, setModalMode] = useState(null); // e.g. 'listings','lease','equipment','storage','partnership','global','post'
  const [feed, setFeed] = useState([]);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);

  // Dummy data for assets + deeper sections
  const assets = [
    {
      id: "plots",
      title: "Buy Agri Plots",
      desc: "Verified agricultural land parcels available for sale across regions.",
      icon: faSeedling,
      action: "Explore Listings",
      img: "https://via.placeholder.com/800x480?text=Agri+Plots",
      listings: [
        { id: "P-101", area: "2.5 acres", price: 2500000, location: "Village A, District X", img: "https://via.placeholder.com/400x250?text=Plot+P-101" },
        { id: "P-202", area: "1.2 acres", price: 1100000, location: "Village B, District Y", img: "https://via.placeholder.com/400x250?text=Plot+P-202" },
      ],
    },

    {
      id: "lease",
      title: "Lease / Rent Land",
      desc: "Short and long-term land leasing options for farming & agri projects.",
      icon: faFileContract,
      action: "Find Land to Lease",
      img: "https://via.placeholder.com/800x480?text=Lease+Land",
      leases: [
        { id: "L-01", area: "1.0 acre", monthly: 8000, term: "6-12 months", location: "Taluk Z" },
        { id: "L-02", area: "0.6 acre", monthly: 4500, term: "3-6 months", location: "Taluk K" },
      ],
    },

    {
      id: "equipment",
      title: "Farm Equipment Rental",
      desc: "Tractors, harvesters, drones, irrigation systems — on rental basis.",
      icon: faTractor,
      action: "View Equipment",
      img: "https://via.placeholder.com/800x480?text=Equipment",
      equipment: [
        { id: "E-T1", name: "Mini Tractor", rateDay: 1200, provider: "GreenRent", img: "https://via.placeholder.com/400x250?text=Tractor" },
        { id: "E-D1", name: "Sprayer Drone", rateDay: 3500, provider: "AeroAg", img: "https://via.placeholder.com/400x250?text=Drone" },
      ],
    },

    {
      id: "storage",
      title: "Warehousing & Cold Storage",
      desc: "Rent or invest in warehouses, silos, and cold storage facilities.",
      icon: faWarehouse,
      action: "Check Facilities",
      img: "https://via.placeholder.com/800x480?text=Storage",
      storage: [
        { id: "S-1", name: "Cold Room A", capacityTons: 15, rentPerDay: 600, location: "Agri Hub" },
        { id: "S-2", name: "Silo B", capacityTons: 40, rentPerDay: 1200, location: "Industrial Zone" },
      ],
    },

    {
      id: "partnership",
      title: "Joint / Contract Farming",
      desc: "Partner with farmers for revenue sharing and sustainable farming.",
      icon: faHandshake,
      action: "Start Partnership",
      img: "https://via.placeholder.com/800x480?text=Partnership",
      programs: [
        { id: "P-agr1", name: "Vegetable Contract", share: "30/70", minArea: "0.5 acres" },
        { id: "P-agr2", name: "Fruit Orchard Partnership", share: "40/60", minArea: "1.0 acres" },
      ],
    },

    {
      id: "global",
      title: "Global Agri Assets",
      desc: "Cross-border agri land and infrastructure investment opportunities.",
      icon: faGlobe,
      action: "View Opportunities",
      img: "https://via.placeholder.com/800x480?text=Global+Assets",
      global: [
        { id: "G-UK-1", country: "Kenya", area: "10 acres", priceUSD: 45000 },
        { id: "G-EU-1", country: "Italy", area: "2 hectares", priceUSD: 120000 },
      ],
    },
  ];

  function announce(text) {
    setFeed(f => [{ ts: Date.now(), text }, ...f].slice(0, 60));
  }

  // Modal handlers and simple forms
  const openModal = (asset, mode) => { setSelected(asset); setModalMode(mode); };
  const closeModal = () => { setSelected(null); setModalMode(null); };

  // Forms state (shared simple forms)
  const [contactForm, setContactForm] = useState({ name: "", phone: "", message: "" });
  const [offerForm, setOfferForm] = useState({ listingId: "", offerAmount: "" });
  const [leaseForm, setLeaseForm] = useState({ leaseId: "", months: 6, name: "", phone: "" });
  const [rentForm, setRentForm] = useState({ equipId: "", days: 1, name: "", phone: "" });
  const [storageForm, setStorageForm] = useState({ storageId: "", days: 1, name: "", phone: "" });
  const [partnershipForm, setPartnershipForm] = useState({ programId: "", name: "", phone: "" });
  const [postAssetForm, setPostAssetForm] = useState({ title: "", desc: "", contact: "" });

  // action handlers
  const handleContact = () => {
    if (!contactForm.name || !contactForm.phone) return alert("Please fill your name & phone to contact owner.");
    announce(`Contact request sent: ${contactForm.name} — ${contactForm.phone}. Message: ${contactForm.message || '(none)'}.`);
    setContactForm({ name: "", phone: "", message: "" });
    closeModal();
    alert('Contact request submitted (dummy). Owner will contact you.');
  };

  const handleOffer = () => {
    if (!offerForm.offerAmount) return alert('Enter offer amount');
    announce(`Offer placed for ${offerForm.listingId || 'listing'}: ₹${offerForm.offerAmount}`);
    setOfferForm({ listingId: "", offerAmount: "" });
    alert('Offer submitted (dummy).');
  };

  const handleLeaseApply = () => {
    if (!leaseForm.name || !leaseForm.phone) return alert('Fill contact info');
    announce(`Lease application ${leaseForm.leaseId} by ${leaseForm.name} for ${leaseForm.months} months`);
    setLeaseForm({ leaseId: "", months: 6, name: "", phone: "" });
    alert('Lease application submitted (dummy).');
  };

  const handleRent = () => {
    if (!rentForm.name || !rentForm.phone) return alert('Fill contact info');
    announce(`Equipment ${rentForm.equipId} rented for ${rentForm.days} days by ${rentForm.name}`);
    setRentForm({ equipId: "", days: 1, name: "", phone: "" });
    alert('Equipment booking created (dummy).');
  };

  const handleStorageBook = () => {
    if (!storageForm.name || !storageForm.phone) return alert('Fill contact info');
    announce(`Storage ${storageForm.storageId} booked for ${storageForm.days} days by ${storageForm.name}`);
    setStorageForm({ storageId: "", days: 1, name: "", phone: "" });
    alert('Storage booking created (dummy).');
  };

  const handlePartnership = () => {
    if (!partnershipForm.name || !partnershipForm.phone) return alert('Fill contact info');
    announce(`Partnership interest ${partnershipForm.programId} by ${partnershipForm.name}`);
    setPartnershipForm({ programId: "", name: "", phone: "" });
    alert('Partnership request submitted (dummy).');
  };

  const handlePostAsset = () => {
    if (!postAssetForm.title || !postAssetForm.contact) return alert('Provide title and contact');
    announce(`New asset posted: ${postAssetForm.title} — Contact: ${postAssetForm.contact}`);
    setPostAssetForm({ title: "", desc: "", contact: "" });
    alert('Your asset has been posted (dummy).');
    closeModal();
  };

  const claimDailyBonus = () => {
    if (dailyBonusClaimed) return alert('Daily bonus already claimed');
    setDailyBonusClaimed(true);
    announce('Daily bonus claimed: ₹50 (dummy)');
    alert('Daily bonus ₹50 added to your account (dummy)');
  };

  // Small helper to format currency
  const fmt = (n) => `₹${n.toLocaleString()}`;

  // Filtered assets by search
  const filtered = assets.filter(a => a.title.toLowerCase().includes(query.toLowerCase()) || a.desc.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen bg-black text-white py-6 px-4 md:px-20">
      <style>{`
        .card-hit{transition: transform .18s ease, box-shadow .18s ease}
        .card-hit:hover{transform: translateY(-6px); box-shadow: 0 18px 40px rgba(2,6,23,0.2)}
      `}</style>

      {/* Header / Hero */}
      <div className="flex flex-col gap-4 mb-2 border border-green-500 py-4 px-4">
          <h1 className="text-xl md:text-xl font-bold text-yellow-200 flex items-center gap-3">🌾 AgriLink - Agri Real Assets Hub</h1>
          <p className="text-white">Marketplace for agricultural land, equipment, warehouses, and partnerships — demo with dummy data. Use cards to explore and interact.</p>
      </div>
      <div className="flex flex-col gap-3 border border-green-400 mt-4 mb-4 py-2 px-4 justify-center">
        <div className="flex rounded px-3 py-2 shadow-sm">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white text-xl w-12 px-0 mt-2" />
          <input placeholder="Enter PIN or locality" className="border border-gray-200 h-10" onChange={e => setQuery(e.target.value)} value={query} />
        </div>
        <div className="flex flex-row gap-2">
          <button onClick={() => openModal(null, 'post')} className="bg-blue-500 text-white hover:bg-yellow-500 px-4 py-2 rounded">Post Your Asset</button>
          <button onClick={claimDailyBonus} className="bg-green-500 text-white px-4 py-2 rounded">Claim Daily Bonus</button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-8">
        {filtered.map((a, idx) => (
          <div key={a.id} className="card-hit rounded-2xl shadow-lg bg-black text-white border border-green-500 p-4 flex flex-col justify-between">
            <img src={a.img} alt={a.title} className="rounded-xl w-full h-40 object-cover mb-3" />
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FontAwesomeIcon icon={a.icon} className="w-7 h-7 text-white" />
                <h3 className="text-m lg:text-lg font-semibold text-yellow-200">{a.title}</h3>
              </div>
              <p className="text-sm text-white mb-3">{a.desc}</p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => openModal(a, a.id === 'plots' ? 'listings' : a.id)} className="flex-1 bg-green-700 text-white rounded-xl py-2">{a.action}</button>
              <button onClick={() => { openModal(a, 'details'); }} className="w-12 bg-blue-500 border rounded-xl flex items-center justify-center">i</button>
            </div>
          </div>
        ))}
      </div>

      {/* Right-side / activity feed (stack below on mobile) */}
      <div className="max-w-6xl mx-auto mb-8">
        <h4 className="text-yellow-100 font-semibold mb-2">Activity Feed</h4>
        <div className="bg-black text-white border border-green-500 rounded-xl p-3 shadow max-h-48 overflow-auto">
          {feed.length === 0 ? <div className="text-yellow-200">No activity yet — actions will appear here.</div> : (
            <ul className="text-sm space-y-2">
              {feed.map((f, i) => (
                <li key={f.ts} className="border-b pb-2">{new Date(f.ts).toLocaleString()} — {f.text}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Modal area (re-usable) */}
      {selected && modalMode === 'details' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-2xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white text-end"><FontAwesomeIcon icon={faTimes} /></button>
            <img src={selected.img} alt={selected.title} className="rounded-xl w-full h-52 object-cover mb-4" />
            <h2 className="text-lg lg:text-xl text-yellow-200 font-bold mb-2">{selected.title}</h2>
            <p className="text-white mb-4">{selected.desc}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => openModal(selected, selected.id === 'plots' ? 'listings' : selected.id)} className="bg-green-700 text-white rounded py-2">Open Section</button>
              <button onClick={() => openModal(selected, 'contact')} className="bg-blue-500 text-white border border-green-500 rounded py-2">Contact Owner</button>
            </div>
          </div>
        </div>
      )}

      {/* Listings Modal */}
      {selected && modalMode === 'listings' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-4xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3">Available Plots / Listings</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {selected.listings.map(l => (
                <div key={l.id} className="border rounded p-3 flex gap-3">
                  <img src={l.img} alt={l.id} className="w-28 h-20 object-cover rounded" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{l.id} • {l.area}</div>
                        <div className="text-sm text-white">{l.location}</div>
                      </div>
                      <div className="text-white font-bold">{fmt(l.price)}</div>
                    </div>
                    <div className="mt-3 flex flex-col gap-2">
                      <button onClick={() => { setOfferForm({ listingId: l.id, offerAmount: '' }); alert('Make Offer: fill amount in the small prompt next.'); }} className="bg-yellow-400 text-white rounded px-3 py-1">Make Offer</button>
                      <button onClick={() => { setContactForm({ name: '', phone: '', message: `Interested in ${l.id}` }); openModal(selected, 'contact'); }} className="bg-green-700 text-white rounded px-3 py-1">Contact Seller</button>
                      <button onClick={() => { announce(`Schedule visited requested for ${l.id}`); alert('Visit scheduled (dummy)'); }} className="bg-blue-500 text-white border rounded px-3 py-1">Schedule Visit</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <h4 className="font-semibold">Quick Offer (demo)</h4>
              <div className="flex flex-col gap-2 mt-2">
                <input placeholder="Listing id (e.g. P-101)" value={offerForm.listingId} onChange={e=> setOfferForm(of=> ({...of, listingId: e.target.value}))} className="p-2 border rounded flex-1" />
                <input placeholder="Offer amount" value={offerForm.offerAmount} onChange={e=> setOfferForm(of=> ({...of, offerAmount: e.target.value}))} className="p-2 border rounded w-40" />
                <button onClick={handleOffer} className="bg-green-700 text-white w-32 rounded px-4">Submit Offer</button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Lease Modal */}
      {selected && modalMode === 'lease' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-3xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-2 mt-2">Lease Options</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {selected.leases.map(l => (
                <div key={l.id} className="border rounded p-3">
                  <div className="font-semibold">{l.area} • {l.term}</div>
                  <div className="text-sm text-white">{l.location}</div>
                  <div className="mt-2">Monthly: <strong>{fmt(l.monthly)}</strong></div>
                  <div className="mt-3 flex flex-row justify-center gap-2">
                    <button onClick={() => { setLeaseForm({...leaseForm, leaseId: l.id}); alert('Fill your contact in the form below to apply.'); }} className="bg-green-700 text-white rounded px-3 py-1">Apply to Lease</button>
                    <button onClick={() => { announce(`Requested details for lease ${l.id}`); alert('Requested lease details (dummy).'); }} className="bg-blue-500 border rounded px-3 py-1">Request Info</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3">
              <h4 className="font-semibold">Lease Application (demo)</h4>
              <div className="flex flex-col justify-start gap-2 mt-2">
                <input placeholder="Lease id" value={leaseForm.leaseId} onChange={e=> setLeaseForm(s=>({...s, leaseId: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Months" type="number" value={leaseForm.months} onChange={e=> setLeaseForm(s=>({...s, months: Number(e.target.value)}))} className="p-2 border rounded text-black" />
                <input placeholder="Your name" value={leaseForm.name} onChange={e=> setLeaseForm(s=>({...s, name: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Phone" value={leaseForm.phone} onChange={e=> setLeaseForm(s=>({...s, phone: e.target.value}))} className="p-2 border rounded text-black" />
                <button onClick={handleLeaseApply} className="flex justify-start w-64 bg-green-700 text-white rounded py-2">Submit Lease Application</button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Equipment Modal */}
      {selected && modalMode === 'equipment' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-4xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3 text-yellow-200">Equipment for Rent</h3>
            <div className="grid gap-3 md:grid-cols-2 place-items-center">
              {selected.equipment.map(eq => (
                <div key={eq.id} className="border rounded p-3 flex gap-3">
                  <img src={eq.img} alt={eq.name} className="w-28 h-20 object-cover rounded" />
                  <div className="flex-1">
                    <div className="font-semibold">{eq.name} • Provider: {eq.provider}</div>
                    <div className="text-sm text-white">Rate/day: <strong>{fmt(eq.rateDay)}</strong></div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => { setRentForm({...rentForm, equipId: eq.id}); alert('Fill rental form below to book (dummy).'); }} className="bg-green-700 text-white rounded px-3 py-1">Rent</button>
                      <button onClick={() => { announce(`Requested equipment ${eq.id} details`); alert('Requested equipment details (dummy).'); }} className="bg-blue-500 text-white border rounded px-3 py-1">Info</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3">
              <h4 className="font-semibold">Rental Booking (demo)</h4>
              <div className="flex flex-col mt-2 gap-2">
                <input placeholder="Equip id" value={rentForm.equipId} onChange={e=> setRentForm(s=>({...s, equipId: e.target.value}))} className="p-2 border rounded" />
                <input placeholder="Days" type="number" value={rentForm.days} onChange={e=> setRentForm(s=>({...s, days: Number(e.target.value)}))} className="p-2 border rounded" />
                <input placeholder="Your name" value={rentForm.name} onChange={e=> setRentForm(s=>({...s, name: e.target.value}))} className="p-2 border rounded" />
                <input placeholder="Phone" value={rentForm.phone} onChange={e=> setRentForm(s=>({...s, phone: e.target.value}))} className="p-2 border rounded" />
                <button onClick={handleRent} className="col-span-4 bg-green-700 text-white w-40 rounded py-2">Book Rental</button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Storage Modal */}
      {selected && modalMode === 'storage' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-3xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3 text-yellow-200">Storage & Warehousing</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {selected.storage.map(s => (
                <div key={s.id} className="border rounded p-3">
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-sm text-white">Capacity: {s.capacityTons} tons • {s.location}</div>
                  <div className="mt-2">Rate/day: <strong>{fmt(s.rentPerDay)}</strong></div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => { setStorageForm({...storageForm, storageId: s.id}); alert('Fill booking form below (dummy).'); }} className="bg-green-700 text-white rounded px-3 py-1">Book</button>
                    <button onClick={() => { announce(`Requested storage ${s.id} info`); alert('Requested storage info (dummy).'); }} className="bg-blue-500 border rounded px-3 py-1">Info</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3">
              <h4 className="font-semibold">Storage Booking (demo)</h4>
              <div className="flex flex-col gap-2 justify-start mt-2">
                <input placeholder="Storage id" value={storageForm.storageId} onChange={e=> setStorageForm(s=>({...s, storageId: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Days" type="number" value={storageForm.days} onChange={e=> setStorageForm(s=>({...s, days: Number(e.target.value)}))} className="p-2 border rounded text-black" />
                <input placeholder="Your name" value={storageForm.name} onChange={e=> setStorageForm(s=>({...s, name: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Phone" value={storageForm.phone} onChange={e=> setStorageForm(s=>({...s, phone: e.target.value}))} className="p-2 border rounded text-black" />
                <button onClick={handleStorageBook} className="w-32 bg-green-700 text-white rounded py-2">Book Storage</button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Partnership Modal */}
      {selected && modalMode === 'partnership' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-3xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3">Joint / Contract Farming Programs</h3>
            <div className="grid gap-3">
              {selected.programs.map(p => (
                <div key={p.id} className="border rounded p-3 flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-white">Share: {p.share} • Min area: {p.minArea}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setPartnershipForm({...partnershipForm, programId: p.id}); alert('Fill the form below to apply.'); }} className="bg-green-700 text-white rounded px-3 py-1">Apply</button>
                    <button onClick={() => { announce(`Requested details for partnership ${p.id}`); alert('Requested partnership info (dummy).'); }} className="bg-blue-500 border rounded px-3 py-1">Info</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3">
              <h4 className="font-semibold">Partnership Application (demo)</h4>
              <div className="flex flex-col gap-2 mt-2">
                <input placeholder="Program id" value={partnershipForm.programId} onChange={e=> setPartnershipForm(s=>({...s, programId: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Your name" value={partnershipForm.name} onChange={e=> setPartnershipForm(s=>({...s, name: e.target.value}))} className="p-2 border rounded text-black" />
                <input placeholder="Phone" value={partnershipForm.phone} onChange={e=> setPartnershipForm(s=>({...s, phone: e.target.value}))} className="p-2 border rounded text-black" />
                <button onClick={handlePartnership} className="col-span-3 bg-green-700 text-white w-60 rounded py-2">Submit Partnership Request</button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Global Modal */}
      {selected && modalMode === 'global' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-3xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-gray-600"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3">Global Opportunities</h3>
            <div className="grid gap-3">
              {selected.global.map(g => (
                <div key={g.id} className="border rounded p-3 flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{g.country} • {g.area}</div>
                    <div className="text-sm text-white">Price USD: {g.priceUSD.toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { announce(`Enquiry for ${g.id} in ${g.country}`); alert('Enquiry sent (dummy).'); }} className="bg-green-700 text-white rounded px-3 py-1">Enquire</button>
                    <button onClick={() => { openModal(selected, 'contact'); setContactForm({ ...contactForm, message: `Enquiry for ${g.id} (${g.country})` }); }} className="bg-blue-500 text-white border rounded px-3 py-1">Contact</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Post Asset Modal */}
      {modalMode === 'post' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-xl w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 w-8 text-white"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mb-3">Post Your Asset (demo)</h3>
            <div className="grid grid-cols-1 gap-2">
              <input placeholder="Title" value={postAssetForm.title} onChange={e=> setPostAssetForm(p=>({...p, title: e.target.value}))} className="p-2 border rounded text-black" />
              <textarea placeholder="Short description" style={{width: "100%", height: "300px"}}value={postAssetForm.desc} onChange={e=> setPostAssetForm(p=>({...p, desc: e.target.value}))} className="p-2 border rounded text-white" />
              <input placeholder="Contact (phone/email)" value={postAssetForm.contact} onChange={e=> setPostAssetForm(p=>({...p, contact: e.target.value}))} className="p-2 border rounded text-black" />
              <div className="flex gap-2">
                <button onClick={handlePostAsset} className="bg-green-700 text-white rounded px-4 py-2">Post Asset</button>
                <button onClick={closeModal} className="bg-blue-500 text-white border rounded px-4 py-2">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal used by various sections */}
      {modalMode === 'contact' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-black text-white border border-green-500 rounded-2xl max-w-md w-full p-4 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 text-white w-8"><FontAwesomeIcon icon={faTimes} /></button>
            <h3 className="text-lg font-bold mt-2 mb-2">Contact Owner / Enquire</h3>
            <div className="grid grid-cols-1 gap-2">
              <input placeholder="Your name" value={contactForm.name} onChange={e=> setContactForm(c=>({...c, name: e.target.value}))} className="p-2 border rounded" />
              <input placeholder="Phone" value={contactForm.phone} onChange={e=> setContactForm(c=>({...c, phone: e.target.value}))} className="p-2 border rounded" />
              <textarea placeholder="Message" style={{width: "100%", height: "300px"}}value={contactForm.message} onChange={e=> setContactForm(c=>({...c, message: e.target.value}))} className="p-2 border rounded" />
              <div className="flex gap-2">
                <button onClick={handleContact} className="bg-green-700 text-white rounded w-20 px-4 py-2">Send</button>
                <button onClick={closeModal} className="bg-blue-500 border rounded w-20 px-4 py-2">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simple footer */}
      <footer className="mt-12 bg-green-700 text-white py-8 px-4 sm:px-6 md:px-20 rounded-2xl">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <a href="#about" className="text-white hover:text-green-300 transition">About</a>
            <a href="#privacy" className="text-white hover:text-green-300 transition">Privacy Policy</a>
            <a href="#terms" className="text-white hover:text-green-300 transition">Terms of Use</a>
            <a href="#contact" className="text-white hover:text-green-300 transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
